import React, { useState, useEffect } from "react";
import axios from "axios";
import Select from "react-select";
import { useParams } from "react-router-dom";
import { useNavigate } from 'react-router-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const likesOptions = [
    { value: "Being Creative and Innovative", label: "Being Creative and Innovative" },
    { value: "Communicating with People", label: "Communicating with People" },
    { value: "Designing and Styling", label: "Designing and Styling" },
    { value: "Helping and Guiding People", label: "Helping and Guiding People" },
    { value: "Learning about other Cultures", label: "Learning about other Cultures" },
    { value: "Media, Glamour and Entertainment", label: "Media, Glamour and Entertainment" },
    { value: "Nature and Outdoors", label: "Nature and Outdoors" },
    { value: "Observing Human Behaviour", label: "Observing Human Behaviour" },
    { value: "Organizing and Structuring", label: "Organizing and Structuring" },
    { value: "Physical Activities and Sports", label: "Physical Activities and Sports" },
    { value: "Playing with Numbers", label: "Playing with Numbers" },
    { value: "Solving Problems", label: "Solving Problems" },
    { value: "Taking Risk and Adventure", label: "Taking Risk and Adventure" },
    { value: "Technology and Gadgets", label: "Technology and Gadgets" },
];

const naturalAbilitiesOptions = [
    { value: "Analytical & Critical thinking", label: "Analytical & Critical thinking" },
    { value: "Computer Skills", label: "Computer Skills" },
    { value: "Dealing with People", label: "Dealing with People" },
    { value: "Drawing and Sketching", label: "Drawing and Sketching" },
    { value: "Imagining Things", label: "Imagining Things" },
    { value: "Making Decisions", label: "Making Decisions" },
    { value: "Planning and Supervising", label: "Planning and Supervising" },
    { value: "Scientific Curiosity", label: "Scientific Curiosity" },
    { value: "Solving Quantitative Problems", label: "Solving Quantitative Problems" },
    { value: "Team Work", label: "Team Work" },
    { value: "Verbal Communication", label: "Verbal Communication" },
    { value: "Working with Machines and Tools", label: "Working with Machines and Tools" },
    { value: "Working with Numerical Data", label: "Working with Numerical Data" },
];

const BasicInfo = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const [formData, setFormData] = useState({
        name: "",
        department_ids: [],
        about_section: "",
        likes: [],
        natural_abilities: [],
        status: "Inactive",
        thumbnail_image: null,
        large_image: null,
    });

    const [departments, setDepartments] = useState([]); 
    const [errors, setErrors] = useState({});
    const [loadingThumbnail, setLoadingThumbnail] = useState(false);
    const [loadingLargeImage, setLoadingLargeImage] = useState(false); 
    const [modalImage, setModalImage] = useState(null); // For viewing images in modal

    useEffect(() => {
        const fetchDepartments = async () => {
            try {
                const response = await axios.get(`${process.env.REACT_APP_API_KEY}career/departments`);
                if (response.data.success && Array.isArray(response.data.data[0])) {
                    const departmentOptions = response.data.data[0].map(dept => ({
                        label: dept.name,
                        value: dept.id
                    }));
                    setDepartments(departmentOptions);
                } else {
                    console.error("API did not return the expected structure");
                }
            } catch (error) {
                console.error("Error fetching departments:", error);
            }
        };
        fetchDepartments();

        if (id) {
            fetchCareerData(id);
        }
    }, [id]);

    const fetchCareerData = async (careerId) => {
        try {
            const response = await axios.post(`${process.env.REACT_APP_API_KEY}career/fetchBasicInfo`, {
                career_id: careerId,
            }, {
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                }
            });

            const data = response.data.data[0];

            // Pre-fill the form with the existing data
            setFormData({
                name: data.name || "",
                department_ids: data.department ? data.department.split(',').map(Number) : [],
                about_section: data.about_section || "",
                likes: likesOptions.filter(option => data.what_all_do_you_like.includes(option.value)),
                natural_abilities: naturalAbilitiesOptions.filter(option => data.what_are_you_naturally_good_at.includes(option.value)),
                status: data.status || "",
                thumbnail_image: data.thumbnail_image || null,
                large_image: data.large_image || null,
            });
        } catch (error) {
            console.error("Error fetching career data:", error);
        } finally {
            
        }
    };
    
    const handleLikesChange = (selectedOptions) => {
        if (selectedOptions.length <= 3) {
            setFormData((prev) => ({ ...prev, likes: selectedOptions }));
        }
    };

    const handleDepartmentChange = (selectedOptions) => {
        const selectedValues = selectedOptions.map(option => option.value); // Store selected department IDs
        setFormData((prev) => ({ ...prev, department_ids: selectedValues }));
    };
    
    
    const handleNaturalAbilitiesChange = (selectedOptions) => {
        if (selectedOptions.length <= 3) {
            setFormData((prev) => ({ ...prev, natural_abilities: selectedOptions }));
        }
    };

    const handleChange = async (e) => {
        const { name, files } = e.target;
        if (files) {
            const file = files[0];
            setFormData((prev) => ({ ...prev, [name]: file }));

            if (name === "thumbnail_image") {
                await uploadImage(file, name);
            } else if (name === "large_image") {
                await uploadImage(file, name);
            }
        } else {
            setFormData((prev) => ({ ...prev, [name]: e.target.value }));
        }
    };

    const uploadImage = async (file, name) => {
        const data = new FormData();
        data.append('image', file);

        if (name === "thumbnail_image") setLoadingThumbnail(true);
        else setLoadingLargeImage(true);

        try {
            const response = await axios.post(`${process.env.REACT_APP_API_KEY}career/uploadFile`, data, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            setFormData((prev) => ({ ...prev, [name]: response.data.link }));

            if (name === "thumbnail_image") setLoadingThumbnail(false);
            else setLoadingLargeImage(false);
        } catch (error) {
            console.error(`${name} upload failed:`, error);
            if (name === "thumbnail_image") setLoadingThumbnail(false);
            else setLoadingLargeImage(false);
        }
    };

    const validate = () => {
        const newErrors = {};
        if (!formData.name) newErrors.name = "Name is required";
        if (!formData.department_ids.length) newErrors.department_ids = "Department is required";
        if (!formData.about_section) newErrors.about_section = "About section is required";
        if (!formData.likes.length) newErrors.likes = "Likes are required";
        if (!formData.natural_abilities.length) newErrors.natural_abilities = "Natural abilities are required";
        if (!formData.status) newErrors.status = "Status is required";
        if (!formData.thumbnail_image) newErrors.thumbnail_image = "Thumbnail image is required";
        if (!formData.large_image) newErrors.large_image = "Large image is required";

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validate()) return;
    
        const data = new URLSearchParams();
    
        Object.keys(formData).forEach((key) => {
            if (Array.isArray(formData[key])) {
                // Join arrays (like department_ids) into a comma-separated string if the API expects that
                const valuesArray = formData[key].map(item => (typeof item === 'object' ? item.value : item));
                data.append(key, valuesArray.join(','));
            } else {
                data.append(key, formData[key]);
            }
        });
    
        // Add or update the career
        if (id) {
            data.append("isEdit", "1");
            data.append("id", id); 
        } else {
            data.append("isEdit", "0");
            data.append("id", "0"); 
        }
    
        try {
            const response = await axios.post(`${process.env.REACT_APP_API_KEY}career/basicInfo`, data.toString(), {
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            });

            if (response.data.success) {
                const careerId = response.data.data;
                window.location.href = `/career-deep-dive/${careerId}?tab=top_college`;
            }

            console.log(response.data);
        } catch (error) {
            console.error("There was an error!", error);
        }
    };
    

    const handleFileChange = async (e, name) => {
        const file = e.target.files[0];
        if (!file) return;
    
        // Validate file type
        const validFileTypes = ['image/jpeg', 'image/png', 'image/jpg'];
        if (!validFileTypes.includes(file.type)) {
            setErrors(prevErrors => ({
                ...prevErrors,
                [name]: 'Invalid file type. Please upload a JPG, JPEG, or PNG image.',
            }));
            return;
        }
    
        // Clear previous errors related to file type
        setErrors(prevErrors => ({
            ...prevErrors,
            [name]: '',
        }));
    
        if (name === "thumbnail_image") {
            setLoadingThumbnail(true);
        } else if (name === "large_image") {
            setLoadingLargeImage(true);
        }
    
        const formData = new FormData();
        formData.append('image', file);
    
        try {
            const response = await axios.post(`${process.env.REACT_APP_API_KEY}career/uploadFile`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
    
            setFormData(prev => ({ ...prev, [name]: response.data.link }));
    
            if (name === "thumbnail_image") {
                setLoadingThumbnail(false);
            } else if (name === "large_image") {
                setLoadingLargeImage(false);
            }
        } catch (error) {
            console.error(`${name} upload failed:`, error);
            if (name === "thumbnail_image") {
                setLoadingThumbnail(false);
            } else if (name === "large_image") {
                setLoadingLargeImage(false);
            }
        }
    };    

    const openImageModal = (imageUrl) => {
        setModalImage(imageUrl); // Open the modal and set the image URL
    };

    const closeImageModal = () => {
        setModalImage(null); // Close the modal by setting it to null
    };

    const handleCancel = () => {
        navigate("/career-list");
    };
    
    const handleAboutSectionChange = (value) => {
        setFormData((prev) => ({ ...prev, about_section: value }));
    };
    
    return (
        <div className="tab-pane fade show active" id="pills-basic-details" role="tabpanel" aria-labelledby="pills-basic-details-tab">
            <div className="bg-white radius-10 p-4 p-sm-5 shadow">
                <div className="row g-4">
                    <div className="col-md-12">
                        <h2 className="fw-semibold font-24">Basic Details</h2>
                    </div>

                    <div className="col-md-12">
                        <label htmlFor="firstName" className="form-label w-100 font-500 mb-1">Name</label>
                        <input
                            id="firstName"
                            type="text"
                            className="form-control height-45"
                            placeholder="Ex: Ashish"
                            maxLength="50"
                            value={formData.name}
                            name="name"
                            onChange={handleChange}
                        />
                        {errors.name && <div className="text-danger">{errors.name}</div>}
                    </div>

                    <div className="col-md-12">
                        <label className="form-label m-0 mb-1 font-16 font-500 w-100" htmlFor="thumbnail_image">Thumbnail Image</label>
                        <input
                            type="file"
                            id="thumbnail_image"
                            name="thumbnail_image"
                            className="form-control height-45"
                            onChange={(e) => handleFileChange(e, "thumbnail_image")}
                        />
                        {errors.thumbnail_image && <div className="text-danger">{errors.thumbnail_image}</div>}
                        <div className="font-14 text-dark-blue opacity-7 text-start mt-0">
                            Only png, jpg, jpeg files. Best resolution 360x190
                        </div>
                        {loadingThumbnail && <div className="text-info mt-2">Uploading thumbnail...</div>}

                        {/* View image below if thumbnail image is present */}
                        {formData.thumbnail_image && (
                            <>
                                <div>
                                    <img
                                        src={formData.thumbnail_image}
                                        alt="Thumbnail Preview"
                                        width="100"
                                        height="100"
                                        style={{ objectFit: 'cover', borderRadius: '10px' }}
                                    />
                                </div>
                                <button
                                    type="button"
                                    className="btn btn-link text-primary"
                                    onClick={() => openImageModal(formData.thumbnail_image)}
                                >
                                    View Image
                                </button>
                            </>
                        )}
                    </div>

                    <div className="col-md-12">
                        <label className="form-label m-0 mb-1 font-16 font-500 w-100" htmlFor="large_image">Large Image</label>
                        <input
                            type="file"
                            id="large_image"
                            name="large_image"
                            className="form-control height-45"
                            onChange={(e) => handleFileChange(e, "large_image")}
                        />
                        {errors.large_image && <div className="text-danger">{errors.large_image}</div>}
                        <div className="font-14 text-dark-blue opacity-7 text-start mt-0">
                            Only png, jpg, jpeg files. Best resolution 370 x 225
                        </div>
                        {loadingLargeImage && <div className="text-info mt-2">Uploading large image...</div>}

                        {/* View image below if large image is present */}
                        {formData.large_image && (
                            <>
                                <div>
                                    <img
                                        src={formData.large_image}
                                        alt="Large Image Preview"
                                        width="150"
                                        height="100"
                                        style={{ objectFit: 'cover', borderRadius: '10px' }}
                                    />
                                </div>
                                <button
                                    type="button"
                                    className="btn btn-link text-primary"
                                    onClick={() => openImageModal(formData.large_image)}
                                >
                                    View Image
                                </button>
                            </>
                        )}
                    </div>


                    <div className="col-md-12">
                        <label htmlFor="department_ids" className="form-label w-100 font-500 mb-1">Departments</label>
                        <Select
                            isMulti
                            name="departments"
                            options={departments}
                            value={departments.filter(dept => formData.department_ids.includes(dept.value))} // Matching by id
                            onChange={handleDepartmentChange}
                        />

                        {errors.department_ids && <div className="text-danger">{errors.department_ids}</div>}
                    </div>

                    <div className="col-md-12">
                        <label htmlFor="about_section" className="form-label w-100 font-500 mb-1">About Section</label>
                        <ReactQuill
                            value={formData.about_section}
                            onChange={handleAboutSectionChange}
                            theme="snow"
                        />
                        {errors.about_section && <div className="text-danger">{errors.about_section}</div>}
                    </div>

                    <div className="col-md-6">
                        <label htmlFor="likes" className="form-label w-100 font-500 mb-1">What all do you like?</label>
                        <Select
                            isMulti
                            name="likes"
                            options={likesOptions}
                            value={formData.likes}
                            onChange={handleLikesChange}
                        />
                        {formData.likes.length === 3 && (
                            <div className="text-info">You have selected the maximum number of likes (3).</div>
                        )}
                        {errors.likes && <div className="text-danger">{errors.likes}</div>}
                    </div>

                    <div className="col-md-6">
                        <label htmlFor="natural_abilities" className="form-label w-100 font-500 mb-1">What are you naturally good at?</label>
                        <Select
                            isMulti
                            name="natural_abilities"
                            options={naturalAbilitiesOptions}
                            value={formData.natural_abilities}
                            onChange={handleNaturalAbilitiesChange}
                        />
                        {formData.natural_abilities.length === 3 && (
                            <div className="text-info">You have selected the maximum number of natural abilities (3).</div>
                        )}
                        {errors.natural_abilities && <div className="text-danger">{errors.natural_abilities}</div>}
                    </div>

                    <div className="col-md-6">
                        <label htmlFor="status" className="form-label w-100 font-500 mb-1">Status</label>
                        <div className="row g-3 align-items-center">
                            <div className="col-auto">
                                <div className="form-check form-check-inline">
                                    <input
                                        className="form-check-input custom-radio me-1"
                                        type="radio"
                                        name="status"
                                        id="status_active"
                                        value="Active"
                                        checked={formData.status === 'Active'}
                                        onChange={handleChange}
                                    />
                                    <label className="form-check-label mb-0 font-16 font-500 mt-1" htmlFor="status_active">Yes</label>
                                </div>
                            </div>
                            <div className="col-auto">
                                <div className="form-check form-check-inline">
                                    <input
                                        className="form-check-input custom-radio me-1"
                                        type="radio"
                                        name="status"
                                        id="status_inactive"
                                        value="Inactive"
                                        checked={formData.status === 'Inactive'}
                                        onChange={handleChange}
                                    />
                                    <label className="form-check-label mb-0 font-16 font-500 mt-1" htmlFor="status_inactive">No</label>
                                </div>
                            </div>
                        </div>
                        {errors.status && <div className="text-danger">{errors.status}</div>}
                    </div>

                    <div className="col-md-12">
                        <div className="action_btns mt-3">
                            <div className="row g-2 align-items-center justify-content-center">
                                <div className="col-auto">
                                    <button type="button" className="btn btn-green height-45 px-4" onClick={handleSubmit}>
                                        <span>Save and Next</span>
                                    </button>
                                </div>
                                <div className="col-auto">
                                    <button type="button" className="btn btn-border-gray height-45 px-5" onClick={handleCancel}>
                                        <span>Cancel </span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {modalImage && (
                        <div className="modal show" style={{ 
                            position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0, 0, 0, 0.8)', display: 'flex', justifyContent: 'center', alignItems: 'center' 
                        }}>
                            <div className="modal-dialog">
                                <div className="modal-content">
                                    <div className="modal-header">
                                        <h5 className="modal-title">Image Preview</h5>
                                        <button type="button" className="btn-close" onClick={closeImageModal}></button>
                                    </div>
                                    <div className="modal-body">
                                        <img src={modalImage} alt="Preview" className="img-fluid" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default BasicInfo;
